import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { StyledAlertBox, ContentStack, StyledText, StyledIcon } from './style';

type ToastType = 'success' | 'error' | 'warning';
type MuiIcon = OverridableComponent<SvgIconTypeMap<{}, "svg">> & { muiName: string; };

const defaultTitles: Record<ToastType, string> = {
    success: "Başarılı",
    warning: "Uyarı",
    error: "Hata",
};

interface AlertProps {
    type: ToastType;
    title?: string;
    message: string;
    onClose: () => void;
    duration?: number;
}

const typeMap: Record<ToastType, { bgColor: string; mainColor: string; Icon: MuiIcon }> = {
    success: { bgColor: '#F6FDF9', mainColor: '#2DC556', Icon: DoneIcon },
    warning: { bgColor: '#FFFBF5', mainColor: '#F59E0B', Icon: WarningAmberIcon },
    error: { bgColor: '#FEF8F8', mainColor: '#EF4444', Icon: ErrorOutlineIcon },
};

const Alert: React.FC<AlertProps> = ({ type, title, message, onClose, duration }) => {
    const { bgColor, mainColor, Icon } = typeMap[type];

    const [isHovered, setIsHovered] = useState(false);

    const defaultDuration = type === 'error' ? 5000 : 3000;
    const autoHideDuration = duration ?? defaultDuration;
    const displayTitle = title ?? defaultTitles[type];

    useEffect(() => {
        if (autoHideDuration > 0 && !isHovered) {
            const timer = setTimeout(() => {
                onClose();
            }, autoHideDuration);

            return () => clearTimeout(timer);
        }
    }, [autoHideDuration, onClose, isHovered]);

    return (
        <StyledAlertBox
            bgColor={bgColor}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <StyledIcon mainColor={mainColor}>
                <Icon sx={{ fontSize: '24px' }} />
            </StyledIcon>

            <ContentStack spacing="4px">
                <StyledText mainColor={mainColor} isTitle>
                    {displayTitle}
                </StyledText>
                <StyledText mainColor={mainColor}>
                    {message}
                </StyledText>
            </ContentStack>

            <StyledIcon mainColor={mainColor} isClose onClick={onClose}>
                <CloseIcon sx={{ fontSize: '24px' }} />
            </StyledIcon>
        </StyledAlertBox>
    );
};

export default Alert;